import { Global } from '@emotion/react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ConfigProvider, RobinTheme } from '@robinpowered/ui-kit';
import { RKTheme } from '@robinpowered/design-system';
import { I18nextProvider } from 'react-i18next';
import { GlobalErrorBoundary, HomepageRedirect } from 'components';
import { i18n } from 'i18n';
import { cssReset } from 'cssReset';
import { config } from 'config';
import {
  AmplitudeProvider,
  ApolloContextProvider,
  AuthContextProvider,
  useApolloContext,
  useAuthContext,
} from 'contexts';
import {
  useAccessToken,
  FeatureFlagProvider,
} from '@robinpowered/dashboard-apps-common';
import { LocalizationContext } from 'contexts/LocalizationContext';
import { AppShell } from 'components/AppShell';
import { isUnauthenticatedPath } from 'constants/unauthPaths';
import { useEffect } from 'react';
import { isUsingParentToken } from 'utils/isUsingParentToken';

export const App = (): JSX.Element => {
  return (
    <I18nextProvider i18n={i18n}>
      <GlobalErrorBoundary>
        <Global styles={cssReset} />
        <RKTheme>
          <ConfigProvider theme={{ ...RobinTheme, cssVar: { key: 'shell' } }}>
            <BrowserRouter>
              <ApolloContextProvider>
                <AuthContextProvider>
                  <LocalizationContext>
                    <RootRouteTree />
                  </LocalizationContext>
                </AuthContextProvider>
              </ApolloContextProvider>
            </BrowserRouter>
          </ConfigProvider>
        </RKTheme>
      </GlobalErrorBoundary>
    </I18nextProvider>
  );
};

const RootRouteTree = (): JSX.Element => {
  const { tenantId } = useApolloContext();
  const { featureFlagUrl, sessionCookieName, dashboardUrl, env } = config;
  const { accessToken } = useAccessToken(sessionCookieName);
  const { loading: authLoading, hasAuthError } = useAuthContext();

  useEffect(() => {
    if (isUsingParentToken()) {
      return;
    }

    // For applications like deliveries that have unauthenticated paths, we don't want to redirect to the login page
    const pathname = window.location.pathname;
    if (isUnauthenticatedPath(pathname)) {
      return;
    }

    if (
      env !== 'development' &&
      (!accessToken || (!authLoading && hasAuthError))
    ) {
      const currentPath = pathname + window.location.search;
      const encodedPath = encodeURIComponent(currentPath);
      window.location.href = `${dashboardUrl}/login?redirectTo=${encodedPath}`;
    }
  }, [authLoading, hasAuthError, dashboardUrl, accessToken, env]);

  return (
    <FeatureFlagProvider
      featureFlagUrl={featureFlagUrl}
      tenantId={tenantId || null}
      accessToken={accessToken}
    >
      <AmplitudeProvider>
        <Routes>
          <Route path="/" element={<HomepageRedirect />} />
          <Route path=":orgSlug" element={<HomepageRedirect />} />
          <Route path=":orgSlug/*" element={<AppShell />} />
        </Routes>
      </AmplitudeProvider>
    </FeatureFlagProvider>
  );
};
